// ========================================== 
// COMPONENTE: SEGUIMIENTO DE CAMPAÑA
// ==========================================
import React, { useState, useEffect } from 'react';

const CampaignTracker = ({ campaign, onClose }) => {
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Cargar datos de seguimiento
  useEffect(() => {
    loadTrackingData();
  }, [campaign.id]);

  const loadTrackingData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/sistemasms/backend/api/campaigns/response-tracking.php?campaign_id=${campaign.id}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setTrackingData(data);
    } catch (err) {
      console.error('Error loading tracking data:', err);
      setError('Error al cargar datos de seguimiento: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Exportar reporte a Excel
  const exportReport = async () => {
    try {
      const response = await fetch(`/sistemasms/backend/api/campaigns/export-report.php?campaign_id=${campaign.id}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Crear blob y descargar
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `Reporte_${campaign.campaign_name.replace(/[^A-Za-z0-9_-]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('✅ Reporte exportado exitosamente');
    } catch (err) {
      console.error('Error exporting report:', err);
      alert('❌ Error al exportar reporte: ' + err.message);
    }
  };

  // Función para obtener el color del estado
  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'text-blue-600 bg-blue-100';
      case 'delivered': return 'text-green-600 bg-green-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando datos de seguimiento...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">❌</div>
            <h3 className="text-lg font-bold text-red-600 mb-2">Error</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <div className="flex gap-2">
              <button 
                onClick={loadTrackingData}
                className="flex-1 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Reintentar
              </button>
              <button 
                onClick={onClose}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { stats, details } = trackingData;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-2">📊 Seguimiento de Campaña</h2>
              <p className="text-purple-100">Campaña: {campaign.campaign_name}</p>
              <p className="text-purple-100 text-sm">Creada: {new Date(campaign.created_at).toLocaleDateString()}</p>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview' 
                  ? 'border-purple-500 text-purple-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              📈 Resumen
            </button>
            <button
              onClick={() => setActiveTab('details')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details' 
                  ? 'border-purple-500 text-purple-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              📋 Detalles por Contacto
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          
          {/* TAB: RESUMEN */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              
              {/* Métricas principales */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="text-2xl font-bold text-blue-600">{stats.total_messages}</div>
                  <div className="text-sm text-blue-800">Total Mensajes</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="text-2xl font-bold text-green-600">{stats.delivered_count}</div>
                  <div className="text-sm text-green-800">Entregados</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <div className="text-2xl font-bold text-purple-600">{stats.responses_count}</div>
                  <div className="text-sm text-purple-800">Respuestas</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <div className="text-2xl font-bold text-orange-600">{stats.delivery_rate}%</div>
                  <div className="text-sm text-orange-800">Tasa Entrega</div>
                </div>
              </div>

              {/* Gráfico de barras simple */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Distribución de Estados</h3>
                <div className="space-y-3">
                  
                  {/* Enviados */}
                  <div className="flex items-center">
                    <div className="w-20 text-sm text-gray-600">Enviados</div>
                    <div className="flex-1 bg-gray-200 rounded-full h-6 mx-3">
                      <div 
                        className="bg-blue-500 h-6 rounded-full flex items-center justify-end pr-2" 
                        style={{width: `${(stats.sent_count / stats.total_messages) * 100}%`}}
                      >
                        <span className="text-white text-xs font-bold">{stats.sent_count}</span>
                      </div>
                    </div>
                    <div className="w-12 text-sm text-gray-600">{Math.round((stats.sent_count / stats.total_messages) * 100)}%</div>
                  </div>

                  {/* Entregados */}
                  <div className="flex items-center">
                    <div className="w-20 text-sm text-gray-600">Entregados</div>
                    <div className="flex-1 bg-gray-200 rounded-full h-6 mx-3">
                      <div 
                        className="bg-green-500 h-6 rounded-full flex items-center justify-end pr-2" 
                        style={{width: `${(stats.delivered_count / stats.total_messages) * 100}%`}}
                      >
                        <span className="text-white text-xs font-bold">{stats.delivered_count}</span>
                      </div>
                    </div>
                    <div className="w-12 text-sm text-gray-600">{Math.round((stats.delivered_count / stats.total_messages) * 100)}%</div>
                  </div>

                  {/* Fallidos */}
                  <div className="flex items-center">
                    <div className="w-20 text-sm text-gray-600">Fallidos</div>
                    <div className="flex-1 bg-gray-200 rounded-full h-6 mx-3">
                      <div 
                        className="bg-red-500 h-6 rounded-full flex items-center justify-end pr-2" 
                        style={{width: `${(stats.failed_count / stats.total_messages) * 100}%`}}
                      >
                        <span className="text-white text-xs font-bold">{stats.failed_count}</span>
                      </div>
                    </div>
                    <div className="w-12 text-sm text-gray-600">{Math.round((stats.failed_count / stats.total_messages) * 100)}%</div>
                  </div>

                  {/* Pendientes */}
                  <div className="flex items-center">
                    <div className="w-20 text-sm text-gray-600">Pendientes</div>
                    <div className="flex-1 bg-gray-200 rounded-full h-6 mx-3">
                      <div 
                        className="bg-yellow-500 h-6 rounded-full flex items-center justify-end pr-2" 
                        style={{width: `${(stats.pending_count / stats.total_messages) * 100}%`}}
                      >
                        <span className="text-white text-xs font-bold">{stats.pending_count}</span>
                      </div>
                    </div>
                    <div className="w-12 text-sm text-gray-600">{Math.round((stats.pending_count / stats.total_messages) * 100)}%</div>
                  </div>

                </div>
              </div>

              {/* Métricas de engagement */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg border border-green-200">
                  <h4 className="text-lg font-semibold text-green-800 mb-2">📈 Tasa de Entrega</h4>
                  <div className="text-3xl font-bold text-green-600 mb-1">{stats.delivery_rate}%</div>
                  <p className="text-green-700 text-sm">
                    {stats.delivered_count} de {stats.total_messages} mensajes entregados
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg border border-purple-200">
                  <h4 className="text-lg font-semibold text-purple-800 mb-2">💬 Tasa de Respuesta</h4>
                  <div className="text-3xl font-bold text-purple-600 mb-1">{stats.response_rate}%</div>
                  <p className="text-purple-700 text-sm">
                    {stats.responses_count} respuestas de {stats.delivered_count} entregados
                  </p>
                </div>
              </div>

            </div>
          )}

          {/* TAB: DETALLES POR CONTACTO */}
          {activeTab === 'details' && (
            <div className="space-y-4">
              
              {/* Filtros y búsqueda */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex flex-wrap gap-4 items-center">
                  <input 
                    type="text" 
                    placeholder="🔍 Buscar por nombre o teléfono..."
                    className="flex-1 min-w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                    <option value="">Todos los estados</option>
                    <option value="sent">Enviados</option>
                    <option value="delivered">Entregados</option>
                    <option value="failed">Fallidos</option>
                    <option value="pending">Pendientes</option>
                  </select>
                  <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                    <option value="">Todas las respuestas</option>
                    <option value="yes">Con respuesta</option>
                    <option value="no">Sin respuesta</option>
                  </select>
                </div>
              </div>

              {/* Tabla de contactos */}
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contacto</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RUT</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enviado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Respuesta</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contenido</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {details.map((contact, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{contact.full_name || 'Sin nombre'}</div>
                            <div className="text-sm text-gray-500">{contact.phone}</div>
                            {contact.email && (
                              <div className="text-xs text-gray-400">{contact.email}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">
                            {contact.rut || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contact.status)}`}>
                            {contact.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {contact.sent_at ? new Date(contact.sent_at).toLocaleString() : 'No enviado'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {contact.response_received ? (
                            <div>
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                ✅ SÍ
                              </span>
                              {contact.response_at && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {new Date(contact.response_at).toLocaleString()}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                              ❌ NO
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">
                            {contact.response_content || 'Sin respuesta'}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {details.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  📭 No hay datos de contactos disponibles
                </div>
              )}

            </div>
          )}

        </div>

        {/* Footer con acciones */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
          <div className="text-sm text-gray-600">
            Total de contactos: {stats.total_messages} | Última actualización: {new Date().toLocaleString()}
          </div>
          <div className="flex gap-3">
            <button
              onClick={exportReport}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              📊 Exportar Excel
            </button>
            <button
              onClick={loadTrackingData}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              🔄 Actualizar
            </button>
            <button
              onClick={onClose}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CampaignTracker;